"use client";

import { useState } from "react";
import { TradeToEarnCampaignStatus, METRICS_EVENTS } from "@cede/types";
import { FeedbackTypeSelector } from "./FeedbackTypeSelector";
import { FEEDBACK_TYPE_CONFIG, FeedbackFormData, FeedbackPayload, FeedbackType } from "./types";
import { TokenIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCampaigns } from "@/hooks/campaign/useCampaigns";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { cn } from "@/lib/utils";
import { Check, Loader2, X } from "lucide-react";
import { useAccount } from "wagmi";

interface FeedbackFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

type SubmitState = "idle" | "loading" | "success" | "error";

export function FeedbackForm({ onClose, onSuccess }: FeedbackFormProps) {
  const { address } = useAccount();
  const { trackEvent } = useMetricsEvents();
  const [formData, setFormData] = useState<FeedbackFormData>({
    type: "general",
    message: "",
    email: "",
    telegram: "",
    campaignId: "",
  });
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [feedbackError, setFeedbackError] = useState("");
  const [contactError, setContactError] = useState("");
  const [campaignError, setCampaignError] = useState("");

  // Fetch live campaigns for PnL and Liquidity issues
  const { campaigns, isLoading: isLoadingCampaigns } = useCampaigns({
    status: [TradeToEarnCampaignStatus.LIVE],
    size: 100,
    skipUserStats: true,
  });

  const currentConfig = FEEDBACK_TYPE_CONFIG[formData.type];
  const pageUrl = typeof window !== "undefined" ? window.location.pathname : "";
  const requiresCampaign = formData.type === "pnl" || formData.type === "liquidity";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const hasMessage = formData.message.trim().length > 0;
    const hasEmail = formData.email.trim().length > 0;
    const hasTelegram = formData.telegram.trim().length > 0;
    const hasCampaign = !requiresCampaign || formData.campaignId.trim().length > 0;

    // Validate all fields and show all errors at once
    const newFeedbackError = !hasMessage ? "Please enter your feedback" : "";
    const newContactError = !hasEmail && !hasTelegram ? "Please provide either an email or Telegram handle" : "";
    const newCampaignError = requiresCampaign && !hasCampaign ? "Please select a campaign" : "";

    setFeedbackError(newFeedbackError);
    setContactError(newContactError);
    setCampaignError(newCampaignError);

    if (newFeedbackError || newContactError || newCampaignError) {
      return;
    }

    setSubmitState("loading");

    // Find selected campaign name if campaign is required
    const selectedCampaign =
      requiresCampaign && campaigns ? campaigns.find((c) => String(c.id) === formData.campaignId) : null;

    const payload: FeedbackPayload = {
      type: formData.type,
      message: formData.message.trim(),
      email: formData.email.trim() || undefined,
      telegram: formData.telegram.trim() || undefined,
      pageUrl,
      walletAddress: address,
      timestamp: new Date().toISOString(),
      campaignId: requiresCampaign ? formData.campaignId : undefined,
      campaignName: selectedCampaign ? selectedCampaign.name : undefined,
    };

    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to send feedback");
      }

      setSubmitState("success");

      // Track successful submission
      trackEvent(METRICS_EVENTS.FEEDBACK_MODAL_SUBMITTED, {
        feedbackType: formData.type,
        campaignId: requiresCampaign ? formData.campaignId : undefined,
        campaignName: selectedCampaign ? selectedCampaign.name : undefined,
      });

      // Close after showing success
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch {
      setSubmitState("error");
      setFeedbackError("Failed to send. Please try again.");
    }
  };

  const handleTypeChange = (type: FeedbackType) => {
    setFormData((prev) => ({ ...prev, type, campaignId: "" }));
    setFeedbackError("");
    setCampaignError("");
  };

  if (submitState === "success") {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3">
        <div className="w-12 h-12 rounded-full bg-cede-success/20 flex items-center justify-center">
          <Check className="w-6 h-6 text-cede-success" />
        </div>
        <p className="text-cede-text-primary font-medium">Thanks for your feedback!</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-cede-text-primary">Share Your Thoughts</h3>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-md text-cede-text-secondary hover:text-cede-text-primary hover:bg-cede-primary/10 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Type Selector */}
      <FeedbackTypeSelector value={formData.type} onChange={handleTypeChange} />

      {/* Campaign Selector - Only for PnL and Liquidity issues */}
      {requiresCampaign && (
        <div>
          <label id="feedback-campaign-label" className="text-xs text-cede-text-secondary mb-2 block">
            Select Campaign <span className="text-cede-error">*</span>
          </label>
          <Select
            value={formData.campaignId}
            onValueChange={(value) => {
              setFormData((prev) => ({ ...prev, campaignId: value }));
              setCampaignError("");
            }}
          >
            <SelectTrigger
              aria-labelledby="feedback-campaign-label"
              className={cn(
                "h-11 rounded-lg border bg-cede-bg-component text-sm transition-all duration-200",
                "border-cede-border-container focus:border-cede-primary/60 focus:ring-2 focus:ring-cede-primary/20",
                campaignError && "border-cede-error/60 focus:border-cede-error focus:ring-cede-error/20",
              )}
            >
              {formData.campaignId && campaigns ? (
                <div className="flex items-center gap-2">
                  {(() => {
                    const selectedCampaign = campaigns.find((c) => String(c.id) === formData.campaignId);
                    return selectedCampaign ? (
                      <>
                        <TokenIcon
                          symbol={selectedCampaign.tokenSymbol || selectedCampaign.name || ""}
                          iconUrl={selectedCampaign.icon}
                          size="sm"
                        />
                        <span>{selectedCampaign.name}</span>
                      </>
                    ) : null;
                  })()}
                </div>
              ) : (
                <SelectValue placeholder={isLoadingCampaigns ? "Loading campaigns..." : "Select a campaign"} />
              )}
            </SelectTrigger>
            <SelectContent>
              {campaigns && campaigns.length > 0 ? (
                campaigns.map((campaign) => (
                  <SelectItem key={campaign.id} value={String(campaign.id)}>
                    <div className="flex items-center gap-2">
                      <TokenIcon
                        symbol={campaign.tokenSymbol || campaign.name || ""}
                        iconUrl={campaign.icon}
                        size="sm"
                      />
                      <span>{campaign.name}</span>
                    </div>
                  </SelectItem>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-cede-text-secondary">No live campaigns available</div>
              )}
            </SelectContent>
          </Select>
          {campaignError && <p className="text-xs text-cede-error mt-1">{campaignError}</p>}
        </div>
      )}

      {/* Message Input */}
      <div>
        <Textarea
          value={formData.message}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, message: e.target.value }));
            setFeedbackError("");
          }}
          placeholder={currentConfig.placeholder}
          className={cn(
            "min-h-[110px] resize-none rounded-lg border border-cede-border-container bg-cede-bg-component text-sm text-cede-text-primary placeholder:text-cede-text-secondary",
            "focus:border-cede-primary/60 focus:ring-2 focus:ring-cede-primary/20",
            feedbackError && "border-cede-error/60 focus:border-cede-error focus:ring-cede-error/20",
          )}
          maxLength={2000}
        />
        <div className="flex justify-between mt-1">
          {feedbackError && <p className="text-xs text-cede-error">{feedbackError}</p>}
          <p className="text-xs text-cede-text-disabled ml-auto">{formData.message.length}/2000</p>
        </div>
      </div>

      {/* Contact Information */}
      <div className="space-y-2">
        <p className="text-xs text-cede-text-secondary">Contact (at least one required)</p>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, email: e.target.value }));
            setContactError("");
          }}
          placeholder="Email"
          className={cn("h-11 text-sm font-medium", contactError && "border-cede-error/60 focus:border-cede-error")}
        />
        <Input
          type="text"
          value={formData.telegram}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, telegram: e.target.value }));
            setContactError("");
          }}
          placeholder="Telegram (e.g., @username)"
          className={cn("h-11 text-sm font-medium", contactError && "border-cede-error/60 focus:border-cede-error")}
        />
        {contactError && <p className="text-xs text-cede-error">{contactError}</p>}
      </div>

      {/* Submit Button */}
      <Button type="submit" variant="primary" size="medium" disabled={submitState === "loading"} className="w-full">
        {submitState === "loading" ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending...
          </>
        ) : (
          "Send Feedback"
        )}
      </Button>
    </form>
  );
}
